import { useQuery } from "@tanstack/react-query";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";
import type { DynamicTable } from "@/types/supabase-helpers";

export interface SidebarBadges {
  [key: string]: number;
}

const EMPTY: SidebarBadges = {};

export const useSidebarBadges = () => {
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["sidebar_badges", orgId],
    queryFn: async (): Promise<SidebarBadges> => {
      if (!orgId) return EMPTY;

      const count = async (table: string, filters: Record<string, unknown>) => {
        let q = (supabase.from as DynamicTable)(table)
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId);
        for (const [k, v] of Object.entries(filters)) {
          if (Array.isArray(v)) q = q.in(k, v);
          else q = q.eq(k, v);
        }
        const { count: c } = await q;
        return c || 0;
      };

      const [contactNew, pipelineNew, _pendingApps, overdueInv, highRisk, openTickets] = await Promise.all([
        count("contact_submissions", { status: "new" }),
        count("crm_pipeline_deals", { stage: "new_lead" }),
        count("membership_applications", { status: "pending" }),
        count("invoices", { status: "overdue" }),
        count("renewal_risks", { tier: ["high_risk", "dormant"] }),
        count("support_tickets", { status: ["open", "in_progress"] }),
      ]);

      return {
        // Map route paths to badge counts — only actionable alerts
        "/crm": contactNew + pipelineNew,
        "/retention": highRisk,
        "/billing": overdueInv,
        "/support": openTickets,
      };
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
};
